import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Link2,
  Lock,
  Pencil,
  RefreshCw,
  Search,
  X,
  Copy,
  Sparkles,
  Undo2,
  History,
  Scale,
  Utensils,
  MoreVertical,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import './IngredientReconciliationRegistry.css';
import { downloadReconciliationExcel, downloadReconciliationPdf } from '../utils/reconciliationExport';
import UnitMappingService from '../services/unitMappingService';
import CrossFamilyConversionModal from './CrossFamilyConversionModal';
import StructuralActionLog from './StructuralActionLog';
import ConvertToPreparationModal from './ConvertToPreparationModal';
import BlockedBySalesNameCheck, { SALES_NAME_CHECK_BLOCK_CODE } from './shared/BlockedBySalesNameCheck';
import { 
  isCrossFamilyChange, 
  UNIT_ID_MAP, 
  formatQuantity, 
  formatUnitLabel, 
  toCanonicalUnit,
  getUnitFamily,
  normalizeToBase,
  STRICT_UNITS
} from '../utils/unitDisplay';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const UNITS = ['kg', 'g', 'L', 'ml', 'pcs'];

const CATEGORY_CLASS = {
  Continental: 'irr-category--continental',
  Indian: 'irr-category--indian',
  Bakery: 'irr-category--bakery',
  Pantry: 'irr-category--pantry'
};

const UNIT_DIM = {
  kg: 'mass',
  g: 'mass',
  L: 'volume',
  ml: 'volume',
  pcs: 'count'
};

const TO_BASE = {
  kg: 1000,
  g: 1,
  L: 1000,
  ml: 1,
  pcs: 1
};


function sameDimFactor(from, to) {
  if (UNIT_DIM[from] !== UNIT_DIM[to]) return null;
  return TO_BASE[from] / TO_BASE[to];
}

// ---------------- Duplicate Detection ----------------
//
// Detection itself lives on the server (GET /reconciliation/duplicates): it reads the
// onboarding gateway's log and re-scans the current ingredients on every call, so the
// list is always freshly derived and identical for every manager looking at it.
// The page's job is to hydrate those id pairs into full rows and let a human resolve
// each one — merge or keep separate.

/**
 * Attach the live row (unit, stock, linkages) to each side of a server-suggested pair.
 * A pair whose item is no longer in the list — merged away, deleted, or filtered out
 * by another session — is dropped rather than rendered with an unusable half.
 */
function hydratePairs(pairs, items) {
  const byId = new Map(items.map((item) => [String(item.id), item]));
  return (pairs || [])
    .map((pair) => {
      const a = byId.get(String(pair.a?.id));
      const b = byId.get(String(pair.b?.id));
      if (!a || !b) return null;
      return { ...pair, a, b };
    })
    .filter(Boolean);
}

function reasonLabel(r) {
  if (r === "exact") return "Same name";
  if (r === "phonetic") return "Sounds the same";
  return "Flagged during setup";
}

function getCombinedStockInDestUnit(source, dest) {
  try {
    const family = getUnitFamily(dest.unit);
    if (!family) return dest.stock + source.stock;
    
    const srcFamily = getUnitFamily(source.unit);
    if (srcFamily !== family) {
      return dest.stock + source.stock;
    }
    
    const { value: destBaseVal } = normalizeToBase(dest.stock, dest.unit);
    const { value: sourceBaseVal } = normalizeToBase(source.stock, source.unit);
    const totalBaseVal = destBaseVal + sourceBaseVal;
    
    const familyConfig = STRICT_UNITS[family];
    if (dest.unit === familyConfig.high) {
      return totalBaseVal / familyConfig.threshold;
    } else {
      return totalBaseVal;
    }
  } catch (e) {
    console.error(e);
    return dest.stock + source.stock;
  }
}

function IngredientReconciliationRegistry({ onSave, onBack, onOpenSalesNameCheck }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [locked, setLocked] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [toast, setToast] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [savedPreparations, setSavedPreparations] = useState([]);
  // Spec §10.2/§10.4: the server refuses every reconciliation route while an
  // onboarding Sales Name Check is in progress. Say so instead of an empty list.
  const [blockedBySnc, setBlockedBySnc] = useState(false);

  // Cross-Family Conversion Modal state (Strict 5 Unit Enforcement)
  const [showCrossFamilyModal, setShowCrossFamilyModal] = useState(false);
  const [crossFamilyModalData, setCrossFamilyModalData] = useState(null);

  // Structural Action Log (renames / unit changes / merges done here, with revert).
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showConvertPrep, setShowConvertPrep] = useState(false);

  // Duplicate detection state — suggestions and dismissals both come from the server,
  // so they survive a reload and are shared across managers.
  const [rawPairs, setRawPairs] = useState([]);
  const [rawDismissed, setRawDismissed] = useState([]);
  const [resolvedMerged, setResolvedMerged] = useState(new Set());
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [duplicateMergePair, setDuplicateMergePair] = useState(null);

  // Implausible-quantity detection. Advisory, not a save gate: unlike a duplicate, an
  // out-of-band quantity can be legitimate (a saffron-grade spice really is measured in
  // fractions of a gram), so it is surfaced for review rather than made blocking.
  const [quantityFlags, setQuantityFlags] = useState(null);
  const [showQuantityFlags, setShowQuantityFlags] = useState(false);

  const quantityFlagCount = quantityFlags?.summary?.flagged_ingredients ?? 0;

  // ⋮ menu in the title row: download the list as Excel or PDF.
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = React.useRef(null);

  useEffect(() => {
    if (!showDownloadMenu) return undefined;
    const onPointerDown = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowDownloadMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showDownloadMenu]);

  const pairs = useMemo(() => hydratePairs(rawPairs, items), [rawPairs, items]);
  const dismissedPairs = useMemo(() => hydratePairs(rawDismissed, items), [rawDismissed, items]);
  const unresolved = pairs.length;

  // Which rows are part of a suggestion, and what each is suggested against — drives
  // the marker on the Merge button and the pre-selected counterpart in the modal.
  const counterpartsByItemId = useMemo(() => {
    const map = new Map();
    pairs.forEach((pair) => {
      const push = (from, to) => {
        const key = String(from.id);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ item: to, reasons: pair.reasons, confidence: pair.confidence, pair });
      };
      push(pair.a, pair.b);
      push(pair.b, pair.a);
    });
    return map;
  }, [pairs]);

  const normalizeSummaryKey = useCallback((value) => String(value || '').trim().toLowerCase(), []);
  
  const savedPreparationByName = useMemo(() => {
    return new Map(
      (savedPreparations || [])
        .filter((prep) => normalizeSummaryKey(prep?.composite_name || prep?.name))
        .map((prep) => [normalizeSummaryKey(prep.composite_name || prep?.name), prep])
    );
  }, [savedPreparations, normalizeSummaryKey]);

  const getAuthHeaders = useCallback(() => {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'X-Business-ID': UnitMappingService.getBusinessId()
    };
  }, []);

  /**
   * Re-derive the suggestion list on the server. Called on open and after every
   * resolution, so a merge by another manager (or a duplicate they just created)
   * shows up here instead of leaving this screen acting on a stale list.
   *
   * Returns the fresh unresolved count so the Save gate can re-check at click time.
   */
  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/duplicates`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (!json?.success) return null;
      setRawPairs(json.data?.pairs || []);
      setRawDismissed(json.data?.dismissed || []);
      return json.data?.pairs?.length ?? 0;
    } catch (err) {
      console.error('Failed to scan for duplicates', err);
      return null;
    }
  }, [getAuthHeaders]);

  /**
   * Ingredients mapped at quantities no kitchen would use. Derived fresh on the server on
   * every call, so a rescale done here (or by another manager) is reflected immediately.
   */
  const fetchQuantityFlags = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/quantity-flags`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (!json?.success) return null;
      setQuantityFlags(json.data);
      return json.data?.summary?.flagged_ingredients ?? 0;
    } catch (err) {
      console.error('Failed to scan ingredient quantities', err);
      return null;
    }
  }, [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = UnitMappingService.getBusinessId();

      const [res, prepRes] = await Promise.all([
        fetch(`${API_URL}/inventory/reconciliation/ingredients`, {
          headers: getAuthHeaders()
        }),
        businessId
          ? fetch(`${API_URL}/medium-features/decomposition/templates?businessId=${businessId}`, {
              headers: getAuthHeaders()
            })
          : Promise.resolve(null)
      ]);

      const json = await res.json().catch(() => null);
      if (res.status === 409 && json && json.code === SALES_NAME_CHECK_BLOCK_CODE) {
        setBlockedBySnc(true);
        return;
      }
      setBlockedBySnc(false);
      if (json && json.success) {
        setItems(json.data);
      }

      if (prepRes && prepRes.ok) {
        const prepJson = await prepRes.json();
        setSavedPreparations(Array.isArray(prepJson?.personalized) ? prepJson.personalized : []);
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    fetchDuplicates();
    fetchQuantityFlags();
  }, [fetchData, fetchDuplicates, fetchQuantityFlags]);

  /** Re-read the ingredient list, the suggestions and the quantity scan after any change. */
  const refreshAll = useCallback(async () => {
    await Promise.all([fetchData(), fetchDuplicates(), fetchQuantityFlags()]);
  }, [fetchData, fetchDuplicates, fetchQuantityFlags]);

  const handleDuplicateMerge = async (pair, keepId) => {
    const keep = String(pair.a.id) === String(keepId) ? pair.a : pair.b;
    const drop = String(pair.a.id) === String(keepId) ? pair.b : pair.a;
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/ingredients/${drop.id}/merge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ targetId: keep.id })
      });
      if (res.ok) {
        await refreshAll();
        setResolvedMerged((prev) => {
          const next = new Set(prev);
          next.add(pair.key);
          return next;
        });
        setSuccessMessage(`Ingredients merged into "${keep.name}" successfully.`);
        setDuplicateMergePair(null);
        setShowDuplicates(true);
      } else {
        // 409 = the merge route refused a cross-family unit pair and explains which
        // recipe blocks it. Nothing was half-written, so the pair stays in the list.
        const body = await res.json().catch(() => null);
        showToast(body?.error || 'Failed to merge duplicates', 'error');
      }
    } catch (err) {
      showToast('Error merging duplicates', 'error');
    }
  };

  const handleKeepSeparate = async (pair) => {
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/duplicates/keep-separate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ itemIdA: pair.a.id, itemIdB: pair.b.id })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        showToast(body?.error || 'Failed to save decision', 'error');
        return;
      }
      await fetchDuplicates();
      setDuplicateMergePair(null);
      setShowDuplicates(true);
    } catch (err) {
      showToast('Error saving decision', 'error');
    }
  };

  const undoKeepSeparate = async (pair) => {
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/duplicates/keep-separate/undo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ itemIdA: pair.a.id, itemIdB: pair.b.id })
      });
      if (!res.ok) {
        showToast('Failed to undo', 'error');
        return;
      }
      await fetchDuplicates();
    } catch (err) {
      showToast('Error undoing decision', 'error');
    }
  };

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return items.filter((item) => 
        activeCategory === 'All' || (item.master_category || 'Uncategorized') === activeCategory
      );
    }
    
    const matched = items.filter((item) => {
      const matchCat = activeCategory === 'All' || (item.master_category || 'Uncategorized') === activeCategory;
      const matchQuery = item.name && item.name.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });

    return [...matched].sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return aName.localeCompare(bName);
    });
  }, [items, query, activeCategory]);

  const dynamicCategories = useMemo(() => {
    const cats = new Set(items.map(i => i.master_category || 'Uncategorized'));
    return Array.from(cats).sort();
  }, [items]);

  const counts = useMemo(() => {
    const next = { All: items.length };
    dynamicCategories.forEach((category) => {
      next[category] = items.filter((item) => (item.master_category || 'Uncategorized') === category).length;
    });
    return next;
  }, [items, dynamicCategories]);

  const grouped = useMemo(() => {
    const next = {};
    dynamicCategories.forEach(c => next[c] = []);
    filteredItems.forEach((item) => {
      const cat = item.master_category || 'Uncategorized';
      if (!next[cat]) next[cat] = [];
      next[cat].push(item);
    });
    return next;
  }, [filteredItems, dynamicCategories]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => setToast(null), 2400);
  };

  /**
   * Names of the business's active complimentary items, normalised like the saved-preparation
   * lookup. Only the PDF uses them (to gather those ingredients into their own section), so
   * they are fetched when a PDF is asked for rather than on every page load.
   */
  const fetchComplimentaryNames = async () => {
    const res = await fetch(`${API_URL}/features/complimentary-recipes?activeOnly=true`, {
      headers: getAuthHeaders()
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.success) {
      throw new Error(json?.error || `HTTP ${res.status}`);
    }
    return new Set(
      (json.complimentaryRecipes || []).map((recipe) => normalizeSummaryKey(recipe?.name)).filter(Boolean)
    );
  };

  /**
   * Download the list as it stands on screen — same tab, same search, same grouping and
   * order, and the unit / qty text the table shows. The row markers (duplicate dot,
   * saved-preparation dot) are carried as fields, since a dot means nothing in a file.
   */
  const handleDownload = async (format) => {
    setShowDownloadMenu(false);

    const visible = dynamicCategories.flatMap((category) =>
      (grouped[category] || []).map((item) => ({ category, item }))
    );
    if (visible.length === 0) {
      showToast('No ingredients to download', 'error');
      return;
    }

    let complimentaryNames = new Set();
    if (format === 'pdf') {
      try {
        complimentaryNames = await fetchComplimentaryNames();
      } catch (err) {
        // Without the list, complimentary items would silently print among the ordinary
        // ingredients — ask for a retry instead of handing over a misleading file.
        console.error('Failed to load complimentary items for the PDF', err);
        showToast('Could not load complimentary items. Please try again.', 'error');
        return;
      }
    }

    const rows = visible.map(({ category, item }) => {
      const key = normalizeSummaryKey(item.name);
      const suggestions = counterpartsByItemId.get(String(item.id)) || [];
      return {
        category,
        name: item.name || '',
        unit: formatUnitLabel(item.unit),
        qty: formatStockDisplay(item.stock, item.unit),
        duplicateOf: suggestions.map((s) => s.item.name).join(', '),
        isSavedPreparation: savedPreparationByName.has(key),
        isComplimentary: complimentaryNames.has(key)
      };
    });

    const q = query.trim();
    const filterLabel = [
      activeCategory !== 'All' ? activeCategory : '',
      q ? `search "${q}"` : ''
    ].filter(Boolean).join(', ');

    try {
      if (format === 'excel') {
        downloadReconciliationExcel(rows, { filterLabel });
      } else {
        downloadReconciliationPdf(rows, { filterLabel });
      }
    } catch (err) {
      console.error('Failed to generate reconciliation download', err);
      showToast('Could not generate the file', 'error');
    }
  };

  /**
   * Write a corrected quantity back to the source row.
   *
   * `quantity` is in the BASE unit the review list displays (g / ml / pcs); the server
   * converts it into whatever unit the row is actually stored in, so a recipe written in
   * kg stays in kg.
   *
   * On success the whole screen is re-read rather than patched locally. One edit can move
   * several rows at once — correcting a preparation component changes every dish that uses
   * it — and a local patch would leave the rest of the list showing figures that are no
   * longer true.
   */
  const handleSaveFlaggedQuantity = async (usage, quantity) => {
    try {
      const res = await fetch(`${API_URL}/inventory/reconciliation/quantity-flags/usage`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: usage.kind,
          menuItemId: usage.menu_item_id,
          prepId: usage.prep_id,
          itemId: usage.item_id,
          quantity,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        showToast(json?.error || 'Could not update the quantity', 'error');
        return false;
      }
      showToast('Quantity updated everywhere it is used', 'success');
      await refreshAll();
      return true;
    } catch (err) {
      console.error('Failed to update quantity', err);
      showToast('Could not update the quantity', 'error');
      return false;
    }
  };

  const handleSave = async () => {
    // Re-check at the moment of Save, not just at page load: a duplicate created by
    // another session after this page opened must not slip through the gate.
    const freshUnresolved = await fetchDuplicates();
    if (freshUnresolved && freshUnresolved > 0) {
      setShowDuplicates(true);
      showToast(
        `${freshUnresolved} unresolved duplicate${freshUnresolved === 1 ? '' : 's'} — merge or dismiss each before saving.`,
        'error'
      );
      return;
    }

    setLocked(true);
    showToast('Registry saved. Stock In dashboard unlocked.');
    if (onSave) {
      await onSave();
    }
  };

  if (blockedBySnc) {
    return (
      <div className="irr-page">
        <header className="irr-header">
          <div className="irr-shell irr-header__inner">
            <div className="irr-title-row">
              <div className="irr-header-left">
                <button type="button" onClick={onBack || undefined} className="irr-back-btn" aria-label="Back">
                  <ArrowLeft size={20} />
                </button>
                <h1>Ingredient Reconciliation</h1>
              </div>
            </div>
          </div>
        </header>
        <div className="irr-shell">
          <BlockedBySalesNameCheck onOpen={onOpenSalesNameCheck} />
        </div>
      </div>
    );
  }

  return (
    <div className="irr-page">
      {toast && (
        <div className={`irr-toast irr-toast--${toast.type}`} role="status">
          {toast.message}
        </div>
      )}

      <header className="irr-header">
        <div className="irr-shell irr-header__inner">
          <div className="irr-title-row">
            <div className="irr-header-left">
              <button type="button" onClick={onBack || undefined} className="irr-back-btn" aria-label="Back">
                <ArrowLeft size={20} />
              </button>
              <h1>Ingredient Reconciliation</h1>
            </div>
            {/* Lives in the title row, not the status row: that row scrolls horizontally
                (overflow-x: auto), which would clip an absolutely positioned dropdown. */}
            <div className="irr-download-menu" ref={downloadMenuRef}>
              <button
                type="button"
                className="irr-download-menu__trigger"
                onClick={() => setShowDownloadMenu((open) => !open)}
                aria-label="Download options"
                aria-haspopup="menu"
                aria-expanded={showDownloadMenu}
                title="Download"
              >
                <MoreVertical size={20} />
              </button>
              {showDownloadMenu && (
                <div className="irr-download-menu__list" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="irr-download-menu__item"
                    onClick={() => handleDownload('excel')}
                  >
                    <FileSpreadsheet size={16} />
                    Download as Excel
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="irr-download-menu__item"
                    onClick={() => handleDownload('pdf')}
                  >
                    <FileText size={16} />
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="irr-status-row">
            <div className="irr-status-left">
              <span className="irr-status-badge">
                {locked ? <CheckCircle2 size={15} /> : <Lock size={15} />}
                {locked ? 'Stock In unlocked' : 'Pre-service review'}
              </span>
            </div>
            <div className="irr-status-actions">
              {/* Bind an ingredient to the saved preparation it is really made from — e.g.
                  "Garam Masala" to "Garam Masala Indian". It sits beside the duplicate tools
                  because it answers the same question the user is already asking here ("these
                  two are the same thing"), with a different answer: not one of them is
                  redundant, but one of them is how the other is made. */}
              <button
                type="button"
                className="irr-secondary-btn"
                onClick={() => setShowConvertPrep(true)}
                title="Make an ingredient from a saved preparation"
              >
                <Utensils size={16} />
                Made in-house
              </button>
              {/* Structural Action Log — every rename / unit change / merge made on this
                  page is recorded and revertable here (chain-locked, last 7 days). */}
              <button
                type="button"
                className="irr-secondary-btn irr-activity-log-btn"
                onClick={() => setShowActivityLog(true)}
                title="View and revert changes made here"
              >
                <History size={16} />
                Activity log
              </button>
              <button
                type="button"
                className="irr-primary-btn irr-save-btn"
                onClick={handleSave}
                disabled={locked || unresolved > 0}
                title={unresolved > 0 ? "Resolve all duplicates to save" : undefined}
              >
                {locked ? (
                  <>
                    <CheckCircle2 size={16} />
                    Saved
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>

          {/* Duplicates Banner */}
          {!locked && unresolved > 0 && (
            <div className="irr-duplicates-banner irr-duplicates-banner--warn">
              <div className="irr-duplicates-banner-text">
                <AlertTriangle size={16} />
                <span>
                  <strong>{unresolved}</strong> possible duplicate{unresolved === 1 ? "" : "s"} found — review before saving.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowDuplicates(true)}
                className="irr-duplicates-banner-btn"
              >
                <Copy size={14} /> Duplicates
              </button>
            </div>
          )}
          {!locked && unresolved === 0 && (
            <div className="irr-duplicates-banner irr-duplicates-banner--success">
              <div className="irr-duplicates-banner-text">
                <Sparkles size={16} />
                <span>No unresolved duplicates. You're clear to save.</span>
              </div>
              <button
                type="button"
                onClick={() => setShowDuplicates(true)}
                className="irr-duplicates-banner-btn irr-duplicates-banner-btn--secondary"
              >
                <Copy size={14} /> Duplicates
              </button>
            </div>
          )}

          {/* Implausible quantities. Advisory — never blocks Save, because an
              out-of-band quantity can be a real recipe, unlike a duplicate. */}
          {!locked && quantityFlagCount > 0 && (
            <div className="irr-duplicates-banner irr-duplicates-banner--warn irr-qty-banner">
              <div className="irr-duplicates-banner-text">
                <AlertTriangle size={16} />
                <span>
                  <strong>{quantityFlagCount}</strong> ingredient{quantityFlagCount === 1 ? '' : 's'} mapped
                  at unusual quantities — worth a look.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowQuantityFlags(true)}
                className="irr-duplicates-banner-btn"
              >
                <Scale size={14} /> Quantities
              </button>
            </div>
          )}

          <label className="irr-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search ingredient..."
              type="search"
            />
          </label>
        </div>
      </header>

      <main className="irr-shell irr-main">



        <div className="irr-tabs" role="tablist" aria-label="Ingredient categories">
          {['All', ...dynamicCategories].map((category) => {
            const active = activeCategory === category;
            return (
              <button
                type="button"
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`irr-tab${active ? ' irr-tab--active' : ''}`}
              >
                {category}
                <span>{counts[category] || 0}</span>
              </button>
            );
          })}
        </div>

        <div className="irr-groups">
          {dynamicCategories.filter((category) => grouped[category] && grouped[category].length > 0).map((category) => (
            <IngredientGroup
              key={category}
              category={category}
              items={grouped[category]}
              onRename={(item) => setModal({ kind: 'rename', item })}
              onMerge={(item) => setModal({ kind: 'merge', item })}
              onUnit={(item) => setModal({ kind: 'unit', item })}
              savedPreparationByName={savedPreparationByName}
              normalizeSummaryKey={normalizeSummaryKey}
              counterpartsByItemId={counterpartsByItemId}
            />
          ))}

          {filteredItems.length === 0 && (
            <div className="irr-empty">No ingredients match this filter.</div>
          )}
        </div>

        <p className="irr-tip">
          Tip: once saved, this registry locks and the daily Stock In (Manual Entry) dashboard unlocks.
        </p>
      </main>

      {modal?.kind === 'rename' && (
        <RenameModal
          item={modal.item}
          onClose={() => setModal(null)}
          onConfirm={async (name, linkagesToKeep) => {
            try {
              const res = await fetch(`${API_URL}/inventory/reconciliation/ingredients/${modal.item.id}/rename`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name, linkagesToKeep })
              });
              if (res.ok) {
                // A rename changes what the scan sees, so re-derive the suggestions
                // as well — the new name may resolve a pair or create one.
                refreshAll();
                setModal(null);
                setSuccessMessage(`Ingredient renamed to "${name}" successfully.`);
              } else {
                showToast('Failed to rename', 'error');
              }
            } catch (err) {
              showToast('Error saving rename', 'error');
            }
          }}
        />
      )}

      {modal?.kind === 'merge' && (
        <MergeModal
          item={modal.item}
          targets={(() => {
            const suggested = counterpartsByItemId.get(String(modal.item.id)) || [];
            const suggestedIds = new Set(suggested.map((s) => String(s.item.id)));
            const sourceCategory = modal.item.master_category || 'Uncategorized';
            const others = items.filter((item) =>
              String(item.id) !== String(modal.item.id) && !suggestedIds.has(String(item.id))
            );
            // Every other ingredient is a valid target. master_category is only the menu
            // category an ingredient happened to be imported under (category_id ->
            // MenuCategories -> MasterMenuCategories), not a property of the ingredient, so
            // filtering on it hid real duplicates — "aromatic powder" (Foods) could not be
            // merged into "tasting salt" (Add ons). Category only decides the ORDER:
            // flagged counterparts first, then same-category, then the rest.
            const sameCategory = others.filter((item) => (item.master_category || 'Uncategorized') === sourceCategory);
            const otherCategory = others.filter((item) => (item.master_category || 'Uncategorized') !== sourceCategory);
            return [...suggested.map((s) => s.item), ...sameCategory, ...otherCategory];
          })()}
          suggestedCounterparts={counterpartsByItemId.get(String(modal.item.id)) || []}
          onClose={() => setModal(null)}
          onConfirm={async (targetId) => {
            try {
              const res = await fetch(`${API_URL}/inventory/reconciliation/ingredients/${modal.item.id}/merge`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ targetId })
              });
              if (res.ok) {
                // refreshAll, not fetchData: a merge resolves any suggestion the pair
                // was part of, so the duplicate list has to be re-derived too.
                refreshAll();
                setModal(null);
                const target = items.find((item) => String(item.id) === String(targetId));
                setSuccessMessage(`Ingredients merged into "${target?.name || 'selected item'}" successfully.`);
              } else {
                // A unit conflict (409) explains exactly which recipe blocks the merge,
                // so show it instead of the generic failure text.
                const body = await res.json().catch(() => null);
                showToast(body?.error || 'Failed to merge', 'error');
              }
            } catch (err) {
              showToast('Error merging items', 'error');
            }
          }}
        />
      )}

      {modal?.kind === 'unit' && (
        <UnitModal
          item={modal.item}
          onClose={() => setModal(null)}
          onConfirm={async (newUnit, factor) => {
            try {
              const currentUnit = modal.item.unit;

              // Canonicalize units to strict symbols (g/kg/ml/L/pcs) using the SAME
              // normalizer the UnitModal uses for its options/dimension logic. Using a
              // different normalizer here caused the cross-family modal to open
              // inconsistently for items stored with non-canonical symbols (gm, ltr, kilogram...).
              const normalizedCurrentUnit = toCanonicalUnit(currentUnit) || currentUnit;
              const normalizedNewUnit = toCanonicalUnit(newUnit) || newUnit;

              // Check if this is a cross-family unit change (Strict 5 Unit Enforcement)
              const isCrossFamily = isCrossFamilyChange(normalizedCurrentUnit, normalizedNewUnit);
              
              if (isCrossFamily) {
                // Open CrossFamilyConversionModal for cross-family changes
                const currentUnitId = Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === normalizedCurrentUnit);
                const newUnitId = Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === normalizedNewUnit);
                
                setCrossFamilyModalData({
                  itemId: modal.item.id,
                  itemName: modal.item.name,
                  currentStock: modal.item.stock,
                  currentUnit: normalizedCurrentUnit,
                  currentUnitId: currentUnitId ? parseInt(currentUnitId) : null,
                  newUnit: normalizedNewUnit,
                  newUnitId: newUnitId ? parseInt(newUnitId) : null
                });
                
                // Close the unit picker modal first
                setModal(null);
                
                // Then open the cross-family conversion modal
                setShowCrossFamilyModal(true);
                
                // Return early - cross-family modal will handle the conversion
                return;
              }
              
              // Same-family changes: apply directly (no conversion needed, just re-scale display)
              // For same-family (e.g., g<->kg or ml<->L), factor is automatic (1000) and handled by backend
              const res = await fetch(`${API_URL}/inventory/reconciliation/ingredients/${modal.item.id}/unit`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ newUnit: normalizedNewUnit, factor })
              });
              
              if (res.ok) {
                fetchData();
                setModal(null);
                setSuccessMessage(`Ingredient unit changed to "${normalizedNewUnit}" successfully.`);
              } else {
                showToast('Failed to update unit', 'error');
              }
            } catch (err) {
              console.error('Unit change error:', err);
              showToast('Error changing unit', 'error');
            }
          }}
        />
      )}

      {/* Cross-Family Conversion Modal (Strict 5 Unit Enforcement) */}
      {showCrossFamilyModal && crossFamilyModalData && (
        <CrossFamilyConversionModal
          isOpen={showCrossFamilyModal}
          itemId={crossFamilyModalData.itemId}
          itemName={crossFamilyModalData.itemName}
          currentStock={crossFamilyModalData.currentStock}
          currentUnit={crossFamilyModalData.currentUnit}
          currentUnitId={crossFamilyModalData.currentUnitId}
          newUnit={crossFamilyModalData.newUnit}
          newUnitId={crossFamilyModalData.newUnitId}
          onConfirm={() => {
            // Close modal and refresh data
            setShowCrossFamilyModal(false);
            setCrossFamilyModalData(null);
            fetchData();
            setSuccessMessage(`Ingredient unit changed to "${crossFamilyModalData.newUnit}" successfully.`);
          }}
          onCancel={() => {
            setShowCrossFamilyModal(false);
            setCrossFamilyModalData(null);
          }}
        />
      )}

      {successMessage && (
        <SuccessModal
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}

      {/* Structural Action Log — activity + revert for changes made on this page.
          hideRetentionWindow drops the "(Last 7 Days)" framing: on the reconciliation
          page (notably during onboarding) the log shows the changes just made here, so
          the 7-day retention wording is noise. */}
      <ConvertToPreparationModal
        isOpen={showConvertPrep}
        onClose={() => setShowConvertPrep(false)}
        onConverted={() => {
          // A conversion changes what recipes consume and what each dish costs, so the
          // ingredient list and its cost columns on this page are now stale.
          refreshAll();
        }}
      />

      <StructuralActionLog
        variant="modal"
        isOpen={showActivityLog}
        onClose={() => setShowActivityLog(false)}
        hideRetentionWindow
        onReverted={() => {
          // A reverted merge re-creates the source item and a reverted rename restores
          // the old name — both change the list and the duplicate suggestions.
          refreshAll();
        }}
      />

      {/* Possible Duplicates Overview Modal */}
      {showDuplicates && (
        <DuplicatesModal
          pairs={pairs}
          dismissedPairs={dismissedPairs}
          resolvedMerged={resolvedMerged}
          showDismissed={showDismissed}
          setShowDismissed={setShowDismissed}
          onClose={() => setShowDuplicates(false)}
          onKeepSeparate={handleKeepSeparate}
          onUndoKeepSeparate={undoKeepSeparate}
          onReviewMerge={(p) => {
            setDuplicateMergePair(p);
            setShowDuplicates(false);
          }}
        />
      )}

      {/* Implausible Quantities Modal */}
      {showQuantityFlags && (
        <QuantityFlagsModal
          data={quantityFlags}
          onClose={() => setShowQuantityFlags(false)}
          onSaveQuantity={handleSaveFlaggedQuantity}
        />
      )}

      {/* Duplicate-specific Merge Modal */}
      {duplicateMergePair && (
        <DuplicateMergeModal
          pair={duplicateMergePair}
          onClose={() => {
            setDuplicateMergePair(null);
            setShowDuplicates(true);
          }}
          onMerge={handleDuplicateMerge}
          onKeep={handleKeepSeparate}
        />
      )}
    </div>
  );
}

function IngredientGroup({ category, items, onRename, onMerge, onUnit, savedPreparationByName, normalizeSummaryKey, counterpartsByItemId }) {
  const catClass = CATEGORY_CLASS[category] || 'irr-category--blue';
  return (
    <section className="irr-group">
      <div className="irr-group__label-row">
        <span className={`irr-category ${catClass}`}>{category}</span>
        <span className="irr-group__count">{items.length} items</span>
      </div>

      <div className="irr-table-card" role="table" aria-label={`${category} ingredients`}>
        <div className="irr-table-head" role="row">
          <span>Ingredients</span>
          <span>Unit</span>
          <span>Qty</span>
          <span>Actions</span>
        </div>

        {items.map((item) => {
          const suggestions = counterpartsByItemId?.get(String(item.id)) || [];
          return (
          <div className="irr-table-row" role="row" key={item.id}>
            <div className="irr-ingredient-cell">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <strong>{item.name}</strong>
                {savedPreparationByName && normalizeSummaryKey && savedPreparationByName.has(normalizeSummaryKey(item.name)) && (
                  <span
                    className="itemmap-prep-dot"
                    title="Saved preparation"
                    aria-label="Saved preparation"
                    style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#FBBF24', flexShrink: 0 }}
                  />
                )}
              </div>
            </div>
            <div className="irr-unit-cell">
              <span className="irr-unit-pill">{formatUnitLabel(item.unit)}</span>
            </div>
            <div className="irr-stock-cell irr-muted">{formatStockDisplay(item.stock, item.unit)}</div>
            <div className="irr-actions">
              <ActionButton icon={Pencil} label="Rename" onClick={() => onRename(item)} />
              <ActionButton
                icon={Link2}
                label="Merge"
                onClick={() => onMerge(item)}
                // Marker for every ingredient that is part of a suggested pair, so the
                // duplicate is visible on the row itself and not only in the modal.
                flagged={suggestions.length > 0}
                flagTitle={
                  suggestions.length > 0
                    ? `Possible duplicate of ${suggestions.map((s) => s.item.name).join(', ')}`
                    : undefined
                }
              />
              <ActionButton icon={RefreshCw} label="Unit" onClick={() => onUnit(item)} />
            </div>
          </div>
          );
        })}
      </div>
    </section>
  );
}

function ActionButton({ icon: Icon, label, onClick, flagged = false, flagTitle }) {
  return (
    <button
      type="button"
      className={`irr-action-btn${flagged ? ' irr-action-btn--flagged' : ''}`}
      onClick={onClick}
      title={flagTitle}
    >
      <Icon size={16} />
      {label}
      {flagged && <span className="irr-action-flag" aria-label="Possible duplicate" />}
    </button>
  );
}

function ModalFrame({ title, description, children, footer, onClose, widthClass = '' }) {
  return (
    <div className="irr-modal-overlay">
      <div className={`irr-modal ${widthClass}`} role="dialog" aria-modal="true" aria-labelledby="irr-modal-title">
        <button type="button" className="irr-modal__close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="irr-modal__header">
          <h2 id="irr-modal-title">{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <div className="irr-modal__body">{children}</div>
        {footer && <div className="irr-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

/** Trim float noise: 0.020000000000000004 must read as 0.02, and 0.0001 must survive. */
function qtyText(value, unit) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  // Small quantities are the whole point of this screen, so they keep their significant
  // digits instead of being rounded away to "0".
  // Number() round-trips away the trailing zeros toPrecision/toFixed leave behind, so
  // 0.0001 reads as "0.0001" rather than "0.00010".
  const text = abs === 0 ? '0'
    : abs < 0.001 ? String(Number(n.toPrecision(2)))
    : abs < 1 ? String(Number(n.toFixed(4)))
    : String(Number(n.toFixed(2)));
  return unit ? `${text} ${unit}` : text;
}

const SEVERITY_LABEL = {
  zero: 'not mapped',
  below: 'too small',
  above: 'too large',
};

/**
 * Review list for ingredients mapped at quantities outside the plausible band.
 *
 * Read-only by design. Every fix here is a different action on a different screen —
 * rescale the recipe line, correct the unit, fix the preparation batch — and offering a
 * one-click "fix" would have to guess which of those the user meant.
 */
/**
 * One editable quantity cell.
 *
 * The value shown and typed is in the BASE unit (g / ml / pcs) — the same basis the band
 * is stated in, so what the reviewer reads is what they are correcting. The server maps it
 * back onto whatever unit the row is stored in.
 *
 * Uncheckable rows have no editor: the scan could not convert their unit, so it has no
 * business writing one back either.
 */
function EditableQuantity({ usage, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const canEdit = !!usage.base_unit && (usage.kind === 'recipe' || usage.kind === 'preparation');

  const begin = () => {
    setValue(usage.base_quantity === null || usage.base_quantity === undefined ? '' : String(usage.base_quantity));
    setEditing(true);
  };

  const commit = async () => {
    const next = Number(value);
    if (!Number.isFinite(next) || next <= 0) return; // the button is disabled; belt and braces
    setSaving(true);
    const ok = await onSave(usage, next);
    setSaving(false);
    // Only close on success — a rejected edit keeps what was typed so it can be corrected
    // rather than retyped from scratch.
    if (ok) setEditing(false);
  };

  if (!canEdit) {
    return <span className="irr-qty-value">{qtyText(usage.base_quantity, usage.base_unit)}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="irr-qty-edit-trigger"
        onClick={begin}
        // The visible text is the number, so without an explicit label the button announces
        // itself as "0.0001 pcs" — a value, with no hint that it can be changed.
        aria-label={`Edit quantity, currently ${qtyText(usage.base_quantity, usage.base_unit)}`}
      >
        <span className="irr-qty-value">{qtyText(usage.base_quantity, usage.base_unit)}</span>
      </button>
    );
  }

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed > 0;

  return (
    <div className="irr-qty-edit">
      <input
        type="number"
        min="0"
        step="any"
        value={value}
        autoFocus
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid) commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        aria-label={`Quantity in ${usage.base_unit}`}
      />
      <span className="irr-qty-edit__unit">{usage.base_unit}</span>
      <button
        type="button"
        onClick={commit}
        disabled={!valid || saving}
        className="irr-qty-edit__ok"
        aria-label="Save quantity"
      >
        {saving ? '…' : <Check size={14} />}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        disabled={saving}
        className="irr-qty-edit__cancel"
        aria-label="Cancel edit"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function QuantityFlagsModal({ data, onClose, onSaveQuantity }) {
  const flagged = data?.flagged || [];
  const summary = data?.summary || {};
  const bands = data?.bands || {};
  const unmeasured = summary.unmeasured_preparations || [];
  const uncheckable = summary.uncheckable_usages || 0;

  // The bands the server actually enforced, in the reader's own units. Stated rather than
  // implied: a range the list is judged against but never shows is not a rule anyone can
  // check their own data against.
  const bandLabels = [
    ['WEIGHT', 'weight'], ['VOLUME', 'volume'], ['COUNT', 'count'],
  ]
    .filter(([key]) => bands[key] && bands[key].minLabel && bands[key].maxLabel)
    .map(([key, label]) => `${label} ${bands[key].minLabel}–${bands[key].maxLabel}`);

  return (
    <ModalFrame
      title="Unusual ingredient quantities"
      onClose={onClose}
      widthClass="irr-modal--wide"
    >
      {/*
        Why one row is flagged at 0.4 g and another is not: the BASIS differs, not the
        number. A recipe ingredient is judged per plate; a preparation's ingredients are
        judged per unit of the preparation itself. Without this sentence the list reads as
        arbitrary, and `bands` was being computed here and thrown away.
      */}
      <p className="irr-qty-basis">
        Ingredients of a preparation are judged per 1 kg / 1 L / 1 pcs of the preparation
        itself, not per plate.
        {bandLabels.length > 0 && (
          <span className="irr-qty-basis__bands"> Expected range — {bandLabels.join('; ')}.</span>
        )}
      </p>

      {flagged.length === 0 && (
        <p className="irr-empty">Every mapped quantity sits inside the expected range.</p>
      )}

      {flagged.map((f) => (
        <div key={f.item_id} className="irr-qty-group">
          <div className="irr-qty-group__head">
            <strong>{f.item_name}</strong>
            <span className="irr-qty-group__unit">{f.item_unit}</span>
            <span className="irr-qty-group__count">
              {f.total_usages} {f.total_usages === 1 ? 'mapping' : 'mappings'}
            </span>
          </div>

          <table className="irr-qty-table">
            <thead>
              <tr>
                <th style={{ width: '40%', textAlign: 'left' }}>Used in</th>
                <th style={{ width: '20%', textAlign: 'center' }}>Quantity</th>
                <th style={{ width: '20%', textAlign: 'center' }}>Basis</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Issue</th>
              </tr>
            </thead>
            <tbody>
              {f.usages.map((u, i) => (
                <tr key={`${f.item_id}-${i}`}>
                  <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                    {u.kind === 'preparation'
                      ? <span className="irr-qty-prep">{u.prep_name}</span>
                      : u.menu_item_name}
                    {/* A broken preparation component is not confined to the preparation —
                        these are the dishes that inherit it. */}
                    {u.affects && u.affects.length > 0 && (
                      <div className="irr-qty-affects">
                        Reaches {u.affects.length} dish{u.affects.length === 1 ? '' : 'es'}:{' '}
                        {u.affects.slice(0, 3).map((a) => a.menu_item_name).join(', ')}
                        {u.affects.length > 3 ? `, +${u.affects.length - 3} more` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <EditableQuantity usage={{ ...u, item_id: f.item_id }} onSave={onSaveQuantity} />
                  </td>
                  <td className="irr-qty-basis" style={{ textAlign: 'center', verticalAlign: 'middle' }}>{u.basis}</td>
                  <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                    <span className={`irr-qty-badge irr-qty-badge--${u.severity}`}>
                      {SEVERITY_LABEL[u.severity] || u.severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {f.usages_hidden > 0 && (
            <p className="irr-qty-more">
              +{f.usages_hidden} more mapping{f.usages_hidden === 1 ? '' : 's'} not shown.
            </p>
          )}
        </div>
      ))}

      {/* What the scan could NOT judge. Stated plainly: a count that looks complete while
          silently skipping rows is worse than one that admits the gap. */}
      {(uncheckable > 0 || unmeasured.length > 0) && (
        <div className="irr-qty-caveat">
          {uncheckable > 0 && (
            <p>
              {uncheckable} mapping{uncheckable === 1 ? '' : 's'} could not be checked because
              {' '}{(summary.uncheckable_units || []).map((u) => u.unit).join(', ')} has no fixed
              conversion to grams or millilitres. Re-enter those in g, ml or pcs to include them.
            </p>
          )}
          {unmeasured.length > 0 && (
            <p>
              {unmeasured.length} preparation{unmeasured.length === 1 ? '' : 's'} ({unmeasured.map((p) => p.prep_name).join(', ')})
              {' '}has no recorded batch size, so its components could not be judged.
            </p>
          )}
        </div>
      )}
    </ModalFrame>
  );
}

function RelatedItemsUsagePanel({ linkages }) {
  const recipes = linkages.filter((l) => l.type === 'recipe');
  const other = linkages.filter((l) => l.type !== 'recipe');

  return (
    <div style={{ minHeight: '132px', border: '1px solid #eef2f7', borderRadius: '14px', background: '#fff', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827', marginBottom: '10px', textAlign: 'left' }}>
        Related items affected by this ingredient
      </div>
      {linkages.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
          {recipes.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Recipes / Preparations</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 160, overflowY: 'auto' }}>
                {recipes.map((item, index) => (
                  <li key={`confirm-recipe-${index}`} style={{ padding: '8px 6px', marginBottom: 0, fontSize: '13px', color: '#333', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' }}>{index + 1}.</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {other.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Other Linked Items</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 160, overflowY: 'auto' }}>
                {other.map((item, index) => (
                  <li key={`confirm-other-${index}`} style={{ padding: '8px 6px', marginBottom: 0, fontSize: '13px', color: '#333', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' }}>{index + 1}.</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'left', minHeight: '64px', display: 'flex', alignItems: 'center' }}>
          No linked recipes, preparations, or complimentary items were found for this item.
        </div>
      )}
    </div>
  );
}

function RenameModal({ item, onClose, onConfirm }) {
  const [name, setName] = useState(item.name);
  const [checked, setChecked] = useState(
    Object.fromEntries(item.linkages.map((linkage) => [linkage.id, true]))
  );
  const [warn, setWarn] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const toggle = (linkage, value) => {
    if (!value && linkage.type === 'recipe') setWarn(linkage.id);
    setChecked((previous) => ({ ...previous, [linkage.id]: value }));
  };

  const handleRenameSubmit = (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) return;
    setShowConfirm(true);
  };

  const activeLinkages = item.linkages.filter((l) => checked[l.id]);

  if (showConfirm) {
    return (
      <div className="irr-modal-overlay" onClick={() => setShowConfirm(false)}>
        <div
          className="irr-modal"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 'min(92vw, 520px)',
            background: '#fff',
            borderRadius: '18px',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
            padding: '22px 22px 18px',
            fontFamily: 'Roboto, sans-serif'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>
              Confirm rename
            </h3>
          </div>

          <div style={{ marginBottom: '14px', padding: '8px 0', borderRadius: 0, border: 'none', background: 'transparent', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{item.name}</div>
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>New</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{name.trim()}</div>
              </div>
            </div>
          </div>

          <RelatedItemsUsagePanel linkages={activeLinkages} />

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#111827',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => onConfirm(name.trim(), Object.keys(checked).filter((k) => checked[k]))}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: '#111827',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Confirm Rename
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalFrame
      title="Change item name"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="irr-secondary-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="irr-primary-btn"
            onClick={handleRenameSubmit}
            disabled={!name.trim()}
          >
            Rename item
          </button>
        </>
      )}
    >
      <form onSubmit={handleRenameSubmit}>
        <label className="irr-field">
          <span>New name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </label>
      </form>

      <div className="irr-link-box">
        <div className="irr-link-box__title">Apply update to</div>
        {item.linkages.map((linkage) => (
          <label className="irr-checkbox-row" key={linkage.id}>
            <input
              type="checkbox"
              checked={checked[linkage.id]}
              onChange={(event) => toggle(linkage, event.target.checked)}
            />
            <span>
              <small>{linkage.type === 'recipe' ? 'item' : linkage.type}:</small> {linkage.label}
            </span>
          </label>
        ))}
      </div>

      {warn && (
        <div className="irr-warning irr-warning--amber">
          <AlertTriangle size={17} />
          <div>
            <p>Unchecking this will clear the ingredient from its recipe card, leaving the dish incomplete.</p>
            <div className="irr-warning__actions">
              <button
                type="button"
                className="irr-secondary-btn irr-small-btn"
                onClick={() => {
                  setChecked((previous) => ({ ...previous, [warn]: true }));
                  setWarn(null);
                }}
              >
                <X size={13} />
                Keep linked
              </button>
              <button type="button" className="irr-ghost-btn irr-small-btn" onClick={() => setWarn(null)}>
                I understand
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalFrame>
  );
}

function MergeModal({ item, targets, onClose, onConfirm, suggestedCounterparts = [] }) {
  // Opened from a flagged row, the counterpart is already chosen — the manager only
  // has to confirm. With more than one suggestion, the first (highest confidence,
  // since the server sorts that way) is pre-selected.
  const [targetId, setTargetId] = useState(suggestedCounterparts[0]?.item?.id ?? '');
  const [showConfirm, setShowConfirm] = useState(false);
  const target = targets.find((candidate) => String(candidate.id) === String(targetId));
  const suggestionForTarget = suggestedCounterparts.find(
    (s) => String(s.item.id) === String(targetId)
  );

  const handleMergeSubmit = () => {
    if (!targetId) return;
    setShowConfirm(true);
  };

  if (showConfirm && target) {
    return (
      <div className="irr-modal-overlay" onClick={() => setShowConfirm(false)}>
        <div
          className="irr-modal"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 'min(92vw, 520px)',
            background: '#fff',
            borderRadius: '18px',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
            padding: '22px 22px 18px',
            fontFamily: 'Roboto, sans-serif'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>
              Confirm merge
            </h3>
          </div>

          <div style={{ marginBottom: '14px', padding: '8px 0', borderRadius: 0, border: 'none', background: 'transparent', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{item.name} ({item.unit})</div>
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Merge into</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{target.name} ({target.unit})</div>
              </div>
            </div>
          </div>

          <div className="irr-preview-box" style={{ marginBottom: '14px' }}>
            <InfoRow label="Stock consolidation">
              {item.stock} {item.unit} + {target.stock} {target.unit} = <strong>{(item.stock + target.stock).toFixed(2)} {target.unit}</strong>
            </InfoRow>
            <InfoRow label="Item linkages">
              <strong>{item.linkages.length}</strong> transferred
            </InfoRow>
          </div>

          <RelatedItemsUsagePanel linkages={item.linkages} />

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#111827',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => onConfirm(targetId)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: '#111827',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Confirm Merge
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalFrame
      title="Merge items"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="irr-secondary-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="irr-primary-btn"
            onClick={handleMergeSubmit}
            disabled={!targetId}
          >
            Merge items
          </button>
        </>
      )}
    >
      <label className="irr-field">
        <span>Merge "{item.name}" into</span>
        <CustomSelect
          value={targetId}
          onChange={(val) => setTargetId(val)}
          options={targets.map((targetItem) => {
            const suggested = suggestedCounterparts.some(
              (s) => String(s.item.id) === String(targetItem.id)
            );
            const targetCategory = targetItem.master_category || 'Uncategorized';
            const crossCategory = targetCategory !== (item.master_category || 'Uncategorized');
            return {
              value: targetItem.id,
              label: `${targetItem.name} (${targetItem.unit})${crossCategory ? ` · ${targetCategory}` : ''}${suggested ? ' — possible duplicate' : ''}`
            };
          })}
          placeholder={targets.length ? 'Select destination item...' : 'No other ingredients'}
        />
      </label>

      {target && (
        <div className="irr-preview-box">
          {suggestionForTarget && (
            <InfoRow label="Reason">
              {suggestionForTarget.reasons.map(reasonLabel).join(', ')}
            </InfoRow>
          )}
          <InfoRow label="Stock consolidation">
            {item.stock} {item.unit} + {target.stock} {target.unit} = <strong>{(item.stock + target.stock).toFixed(2)} {target.unit}</strong>
          </InfoRow>
          <InfoRow label="Item linkages">
            <strong>{item.linkages.length}</strong> transferred
          </InfoRow>
        </div>
      )}
    </ModalFrame>
  );
}

function UnitModal({ item, onClose, onConfirm }) {
  const [unit, setUnit] = useState('');
  const [customFactor, setCustomFactor] = useState('1');
  const [showConfirm, setShowConfirm] = useState(false);

  const currentUnit = toCanonicalUnit(item.unit) || item.unit;
  const sameDim = unit ? sameDimFactor(currentUnit, unit) : null;
  const isCross = unit && unit !== currentUnit && sameDim === null;

  const factor = useMemo(() => {
    if (!unit || unit === currentUnit) return null;
    if (sameDim !== null) return sameDim;
    const parsed = parseFloat(customFactor);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [unit, currentUnit, sameDim, customFactor]);

  const conversion = useMemo(() => {
    if (factor == null) return null;
    return {
      stock: Number((item.stock * factor).toFixed(4)),
      rate: Number((item.rate / factor).toFixed(4))
    };
  }, [factor, item]);

  const getNewUnitOptions = (currentUnit) => {
    if (currentUnit === 'kg' || currentUnit === 'g') {
      return ['ml', 'L', 'pcs'];
    }
    if (currentUnit === 'L' || currentUnit === 'ml') {
      return ['kg', 'g', 'pcs'];
    }
    return UNITS.filter(u => u !== currentUnit);
  };

  const handleUnitSubmit = () => {
    if (!unit || unit === currentUnit || !conversion) return;
    setShowConfirm(true);
  };

  if (showConfirm && conversion) {
    return (
      <div className="irr-modal-overlay" onClick={() => setShowConfirm(false)}>
        <div
          className="irr-modal"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 'min(92vw, 520px)',
            background: '#fff',
            borderRadius: '18px',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
            padding: '22px 22px 18px',
            fontFamily: 'Roboto, sans-serif'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>
              Confirm unit change
            </h3>
          </div>

          <div style={{ marginBottom: '14px', padding: '8px 0', borderRadius: 0, border: 'none', background: 'transparent', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{currentUnit}</div>
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>New unit</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{unit}</div>
              </div>
            </div>
          </div>

          <div className="irr-preview-box" style={{ marginBottom: '14px' }}>
            <div className="irr-link-box__title">Recalculation preview</div>
            <InfoRow label="Warehouse stock">
              {item.stock} {currentUnit} -> <strong>{conversion.stock} {unit}</strong>
            </InfoRow>
            <InfoRow label="Purchase rate">
              Rs {item.rate}/{currentUnit} -> <strong>Rs {conversion.rate}/{unit}</strong>
            </InfoRow>
          </div>

          <RelatedItemsUsagePanel linkages={item.linkages} />

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#111827',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => onConfirm(unit, factor)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: '#111827',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Confirm Change
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalFrame
      title="Change item unit"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="irr-secondary-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="irr-primary-btn"
            onClick={handleUnitSubmit}
            disabled={!unit || unit === currentUnit || !conversion}
          >
            Change unit
          </button>
        </>
      )}
    >
      <div className="irr-unit-grid">
        <label className="irr-field">
          <span>Current</span>
          <input value={currentUnit} readOnly />
        </label>
        <label className="irr-field">
          <span>New unit</span>
          <CustomSelect
            value={unit}
            onChange={(val) => setUnit(val)}
            options={getNewUnitOptions(currentUnit).map((unitOption) => ({
              value: unitOption,
              label: unitOption
            }))}
            placeholder="Select unit..."
            showSearch={false}
          />
        </label>
      </div>

      {unit && unit !== currentUnit && sameDim !== null && (
        <div className="irr-preview-box">
          <div className="irr-link-box__title">Standard conversion</div>
          <InfoRow label="Factor">
            1 {currentUnit} = <strong>{sameDim} {unit}</strong>
          </InfoRow>
        </div>
      )}

      {isCross && (
        <div className="irr-cross-dimension-box">
          <strong>Cross-dimension conversion ({UNIT_DIM[currentUnit]} -> {UNIT_DIM[unit]})</strong>
          <label className="irr-factor-row">
            <span>1 {currentUnit} =</span>
            <input
              type="number"
              step="0.0001"
              min="0"
              value={customFactor}
              onChange={(event) => setCustomFactor(event.target.value)}
            />
            <span>{unit}</span>
          </label>
        </div>
      )}

      {unit && unit !== currentUnit && conversion && (
        <div className="irr-preview-box">
          <div className="irr-link-box__title">Recalculation preview</div>
          <InfoRow label="Warehouse stock">
            {item.stock} {currentUnit} -> <strong>{conversion.stock} {unit}</strong>
          </InfoRow>
          <InfoRow label="Purchase rate">
            Rs {item.rate}/{currentUnit} -> <strong>Rs {conversion.rate}/{unit}</strong>
          </InfoRow>
          <InfoRow label="Items affected">
            <strong>{item.linkages.filter((linkage) => linkage.type === 'recipe').length}</strong> auto-converted
          </InfoRow>
        </div>
      )}
    </ModalFrame>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="irr-info-row">
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
}

// Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
const formatStockDisplay = (stock, unit) => {
  if (stock == null) return '';
  const numStock = Number(stock);
  if (isNaN(numStock)) return `${stock} ${unit || ''}`;
  return formatQuantity(numStock, unit);
};

function CustomSelect({ value, onChange, options, placeholder, showSearch = true, direction = 'auto' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, bottom: 'auto', left: 0, width: 0, isUpward: false });
  const triggerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const searchRef = React.useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Auto-focus search when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current && searchRef.current.focus(), 50);
    }
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpward = direction === 'up' || (direction === 'auto' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

      if (openUpward) {
        setDropdownPos({
          bottom: window.innerHeight - rect.top + 4,
          top: 'auto',
          left: rect.left,
          width: rect.width,
          isUpward: true,
        });
      } else {
        setDropdownPos({
          top: rect.bottom + 4,
          bottom: 'auto',
          left: rect.left,
          width: rect.width,
          isUpward: false,
        });
      }
    }
    setIsOpen((prev) => !prev);
    if (isOpen) setSearchQuery('');
  };

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    const q = searchQuery.toLowerCase();
    if (!q) return 0;
    const aStarts = a.label.toLowerCase().startsWith(q);
    const bStarts = b.label.toLowerCase().startsWith(q);
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return 0;
  });

  return (
    <div className="irr-custom-select-container">
      <button
        type="button"
        className="irr-custom-select-trigger"
        ref={triggerRef}
        onClick={handleToggle}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown size={16} />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`irr-custom-select-options ${dropdownPos.isUpward ? 'irr-custom-select-options--up' : ''}`}
          style={{
            position: 'fixed',
            top: dropdownPos.isUpward ? 'auto' : `${dropdownPos.top}px`,
            bottom: dropdownPos.isUpward ? `${dropdownPos.bottom}px` : 'auto',
            left: `${dropdownPos.left}px`,
            width: `${dropdownPos.width}px`,
            zIndex: 99999,
            backgroundColor: '#ffffff',
            boxShadow: dropdownPos.isUpward
              ? '0 -10px 25px -5px rgba(0, 0, 0, 0.15), 0 -8px 10px -6px rgba(0, 0, 0, 0.1)'
              : '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Search bar inside dropdown */}
          {showSearch && (
            <div className="irr-custom-select-search-wrap">
              <Search size={14} className="irr-custom-select-search-icon" />
              <input
                ref={searchRef}
                type="text"
                className="irr-custom-select-search-input"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="irr-custom-select-search-clear"
                  onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); searchRef.current && searchRef.current.focus(); }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}
          <div className="irr-custom-select-options-list">
            {filteredOptions.length === 0 ? (
              <div className="irr-custom-select-no-results">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`irr-custom-select-option${String(option.value) === String(value) ? ' irr-custom-select-option--selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchQuery('');
                  }}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function SuccessModal({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 2000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="irr-modal-overlay" onMouseDown={onClose}>
      <div className="irr-success-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="irr-success-modal__icon-circle">
          <Check size={36} strokeWidth={3} />
        </div>
        <h3 className="irr-success-modal__title">Success</h3>
        <p className="irr-success-modal__message">{message}</p>
        <button type="button" className="irr-success-modal__btn" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

function DuplicatesModal({
  pairs,
  dismissedPairs,
  resolvedMerged,
  showDismissed,
  setShowDismissed,
  onClose,
  onKeepSeparate,
  onUndoKeepSeparate,
  onReviewMerge
}) {
  return (
    <ModalFrame
      title="Possible duplicates"
      onClose={onClose}
    >
      <div className="irr-duplicates-modal-top" style={{ display: 'flex', justifyContent: 'flex-start', width: '100%', marginBottom: '10px' }}>
        <button
          type="button"
          onClick={() => setShowDismissed((v) => !v)}
          className="irr-duplicates-dismissed-toggle"
        >
          {showDismissed ? "Hide" : "Show"} dismissed ({dismissedPairs.length})
        </button>
      </div>

      {pairs.length === 0 && (
        <div className="irr-duplicates-empty">
          No suggested duplicates.
        </div>
      )}

      <ul className="irr-duplicates-list">
        {pairs.map((p) => (
          <li key={p.key} className="irr-duplicates-item">
            <div className="irr-duplicates-item-info">
              <div className="irr-duplicates-grid">
                {/* Row 1: Names and Arrow */}
                <div className="irr-duplicates-grid-name-a">
                  <strong>{p.a.name}</strong>
                </div>
                <div className="irr-duplicates-grid-arrow">
                  <span className="irr-duplicates-arrow">↔</span>
                </div>
                <div className="irr-duplicates-grid-name-b">
                  <strong>{p.b.name}</strong>
                </div>

                {/* Row 2: Badges */}
                <div className="irr-duplicates-grid-badge-a">
                  <span className={`irr-duplicates-badge irr-duplicates-badge--${p.confidence}`}>
                    {p.confidence === "high" ? "High confidence" : "Medium confidence"}
                  </span>
                </div>
                <div className="irr-duplicates-grid-spacer" />
                <div className="irr-duplicates-grid-badge-b">
                  {p.reasons.map((r) => (
                    <span key={r} className="irr-duplicates-reason">
                      {reasonLabel(r)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="irr-duplicates-item-actions">
              <button
                type="button"
                onClick={() => onKeepSeparate(p)}
                className="irr-duplicates-action-btn irr-duplicates-action-btn--secondary"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={() => onReviewMerge(p)}
                className="irr-duplicates-action-btn irr-duplicates-action-btn--primary"
              >
                Merge
              </button>
            </div>
          </li>
        ))}
      </ul>

      {showDismissed && dismissedPairs.length > 0 && (
        <div className="irr-duplicates-dismissed-section">
          <div className="irr-duplicates-dismissed-title">
            Dismissed pairs
          </div>
          <ul className="irr-duplicates-dismissed-list">
            {dismissedPairs.map((pair) => (
              <li key={pair.key} className="irr-duplicates-dismissed-item">
                <span>
                  {pair.a.name} ↔ {pair.b.name}
                </span>
                <button
                  type="button"
                  onClick={() => onUndoKeepSeparate(pair)}
                  className="irr-duplicates-undo-btn"
                >
                  <Undo2 size={12} style={{ marginRight: '4px' }} /> Undo
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {resolvedMerged.size > 0 && (
        <div className="irr-duplicates-merged-alert">
          Merged {resolvedMerged.size} pair{resolvedMerged.size === 1 ? "" : "s"} this session.
        </div>
      )}
    </ModalFrame>
  );
}

function DuplicateMergeModal({
  pair,
  onClose,
  onMerge,
  onKeep,
}) {
  const [keepId, setKeepId] = useState(pair.b.id); // default: merge A into B
  const [showConfirm, setShowConfirm] = useState(false);
  
  const source = String(pair.a.id) === String(keepId) ? pair.b : pair.a;
  const dest = String(pair.a.id) === String(keepId) ? pair.a : pair.b;
  
  const totalStock = getCombinedStockInDestUnit(source, dest);
  
  if (showConfirm) {
    return (
      <div className="irr-modal-overlay" onClick={() => setShowConfirm(false)}>
        <div
          className="irr-modal"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 'min(92vw, 520px)',
            background: '#fff',
            borderRadius: '18px',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
            padding: '22px 22px 18px',
            fontFamily: 'Roboto, sans-serif',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>
              Confirm merge
            </h3>
          </div>

          <div style={{ marginBottom: '14px', padding: '8px 0', borderRadius: 0, border: 'none', background: 'transparent', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{source.name} ({formatUnitLabel(source.unit)})</div>
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Merge into</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{dest.name} ({formatUnitLabel(dest.unit)})</div>
              </div>
            </div>
          </div>

          <div className="irr-preview-box" style={{ marginBottom: '14px' }}>
            <InfoRow label="Stock consolidation">
              {formatQuantity(dest.stock, dest.unit)} + {formatQuantity(source.stock, source.unit)} = <strong>{formatQuantity(totalStock, dest.unit)}</strong>
            </InfoRow>
            <InfoRow label="Item linkages">
              <strong>{source.linkages?.length || 0}</strong> transferred
            </InfoRow>
          </div>

          <RelatedItemsUsagePanel linkages={source.linkages || []} />

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#111827',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => onMerge(pair, keepId)}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: '#111827',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Confirm Merge
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalFrame
      title="Merge items"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="irr-secondary-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="irr-primary-btn"
            onClick={() => setShowConfirm(true)}
          >
            Merge items
          </button>
        </>
      )}
    >
      <div className="irr-dup-merge-title">
        Merge "{source.name}" into
      </div>
      <div className="irr-dup-merge-field">
        <CustomSelect
          value={keepId}
          onChange={(val) => setKeepId(val)}
          options={[
            {
              value: pair.b.id,
              label: `${pair.b.name} (${formatUnitLabel(pair.b.unit)})`
            },
            {
              value: pair.a.id,
              label: `${pair.a.name} (${formatUnitLabel(pair.a.unit)})`
            }
          ]}
          showSearch={false}
        />
      </div>

      <div className="irr-dup-merge-preview" style={{ border: '1px solid #e2e8f0', borderRadius: '10px', background: 'rgba(248, 250, 252, 0.72)', padding: '12px 14px', marginTop: '16px' }}>
        <div className="irr-dup-merge-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
          <span className="irr-dup-merge-label" style={{ color: '#64748b', fontSize: '14px' }}>Reason</span>
          <div className="irr-dup-merge-reasons" style={{ display: 'flex', gap: '6px' }}>
            {pair.reasons.map((r) => (
              <span key={r} className="irr-duplicates-reason" style={{ borderRadius: '999px', background: '#f1f5f9', padding: '2px 8px', fontSize: '11px', color: '#475569' }}>
                {reasonLabel(r)}
              </span>
            ))}
          </div>
        </div>
        <div className="irr-dup-merge-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
          <span className="irr-dup-merge-label" style={{ color: '#64748b', fontSize: '14px' }}>Stock consolidation</span>
          <span style={{ fontSize: '14px', color: '#020817' }}>
            {formatQuantity(dest.stock, dest.unit)} + {formatQuantity(source.stock, source.unit)} ={" "}
            <strong>{formatQuantity(totalStock, dest.unit)}</strong>
          </span>
        </div>
        <div className="irr-dup-merge-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
          <span className="irr-dup-merge-label" style={{ color: '#64748b', fontSize: '14px' }}>Item linkages</span>
          <span style={{ fontSize: '14px', color: '#020817' }}>
            <strong>{source.linkages?.length || 0}</strong> transferred
          </span>
        </div>
      </div>

      <div className="irr-dup-merge-footer-link" style={{ marginTop: '20px', textAlign: 'left' }}>
        <button
          type="button"
          onClick={() => onKeep(pair)}
          className="irr-dup-merge-link-btn"
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '13px', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
        >
          Not a duplicate — keep separate
        </button>
      </div>
    </ModalFrame>
  );
}

export default IngredientReconciliationRegistry;
